import { notifications } from '@mantine/notifications';
import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';
import { create } from 'zustand';
import {
  type Balance,
  type FormSummary,
  LOGIN_METHOD,
  type LoginChallengeUserResponse,
  RustyAuth,
  RustyFormsApi,
  type Session,
  type StateBaseWithSubscription,
  type VerifiedEmailsResponse,
  type Verify2FALoginRequest,
} from '@/api';
import { createErrorHandler, createSuccessHandler } from '@/api/utils/notification-handlers';
import { API_BASE_URL, LOCAL_STORAGE_KEY } from './constants';
import { clearDecryptedMessagesCache } from './lib/nostr-decrypt';
import { clearPostLoginPath } from './lib/post-login';

const errorHandler = createErrorHandler({
  notifications,
  onError: (error: AxiosError) => {
    Sentry.captureException(error, {
      extra: {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        data: error.response?.data,
      },
    });

    console.log('API Error:', error);
  },
});

const successHandler = createSuccessHandler({
  notifications,
});

// Wired into `RustyAuth` via `onSessionChange` so any session mutation
// (login, refresh, whoami, logout) updates Sentry without each call site
// having to remember.
const updateSentryUserContext = (session: Session | null | undefined) => {
  if (session?.isLoggedIn) {
    Sentry.setUser({
      id: session.userId,
      email: session.email,
      username:
        session.displayName ??
        (session.publicKey
          ? `${session.publicKey.slice(0, 8)}...${session.publicKey.slice(-8)}`
          : session.userId),
    });

    Sentry.setTag('login_method', session.method || 'unknown');
    Sentry.setTag('user_authenticated', 'true');
  } else {
    Sentry.setUser(null);
    Sentry.setTag('user_authenticated', 'false');
  }
};

interface State extends StateBaseWithSubscription<RustyFormsApi> {
  getAndSetVerifiedEmails: () => Promise<VerifiedEmailsResponse>;
  /** Signs out locally without calling the server, for when the session is already gone there. */
  clearLocalSession: () => Promise<void>;

  /** Null until the first refresh succeeds. */
  unreadCount: number | null;
  /** Problems in the "action" group, muted ones excluded. */
  problemsCount: number | null;
  formsSummary: FormSummary[] | null;
  accountBalance: Balance[] | null;
  refreshUnreadCount: () => Promise<void>;
  refreshProblemsCount: () => Promise<void>;
  refreshFormsSummary: () => Promise<FormSummary[] | null>;
  refreshAccountBalance: () => Promise<void>;
}

const ACCOUNT_RESET = {
  verifiedEmails: [],
  currentSubscription: null,
  unreadCount: null,
  problemsCount: null,
  formsSummary: null,
  accountBalance: null,
} satisfies Partial<State>;

const api = new RustyFormsApi({
  baseUrl: API_BASE_URL,
  auth: new RustyAuth({
    baseUrl: API_BASE_URL,
    useLocalStore: true,
    localStorageKey: LOCAL_STORAGE_KEY,
    onSessionChange: updateSentryUserContext,
  }),
  errorHandler,
  successHandler,
});

export const useRustyState = create<State>((set, get) => ({
  init: () => {
    const session = get().getSession();
    updateSentryUserContext(session);
  },
  session: {
    isLoggedIn: false,
  },
  getSession: (): Session | undefined => {
    if (api.auth) {
      return api.auth.getSession();
    }
    console.warn('No auth instance available to get session');
  },
  login: async (identifier: string, loginMethod: LOGIN_METHOD) => {
    if (!api.auth) {
      throw new Error('No auth');
    }

    switch (loginMethod) {
      case LOGIN_METHOD.NOSTR:
        return await api.auth.login({
          type: LOGIN_METHOD.NOSTR,
          content: {
            public_key: identifier,
          },
        });
      case LOGIN_METHOD.EMAIL_MAGIC_LINK:
        return await api.auth.login({
          type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
          content: {
            email: identifier,
          },
        });
      default:
        throw new Error(`Unsupported login method: ${loginMethod}`);
    }
  },
  loginChallenge: async (loginResponse: LoginChallengeUserResponse) => {
    if (!api.auth) {
      throw new Error('No auth');
    }

    if (loginResponse.type === LOGIN_METHOD.EMAIL_MAGIC_LINK) {
      return await api.auth.loginChallenge({
        type: LOGIN_METHOD.EMAIL_MAGIC_LINK,
        content: {
          id: loginResponse.content.id,
          challenge: loginResponse.content.challenge,
        },
      });
    } else if (loginResponse.type === LOGIN_METHOD.NOSTR) {
      return await api.auth.loginChallenge(loginResponse);
    }
    throw new Error('Unsupported challenge response');
  },
  verify2FA: async (request: Verify2FALoginRequest) => {
    if (!api.auth) {
      throw new Error('No auth');
    }
    return await api.auth.verify2FA(request);
  },
  logout: async () => {
    // Clear decrypted messages cache on logout. Sentry context clears
    // automatically via the `onSessionChange` hook registered on RustyAuth.
    // Runs first: once logout hands the browser to the IdP, the page unloads.
    await clearDecryptedMessagesCache();
    clearPostLoginPath();
    set(ACCOUNT_RESET);

    if (api.auth) {
      return api.auth.logout();
    }
    console.warn('No auth instance available to logout');
    return false;
  },
  clearLocalSession: async () => {
    await clearDecryptedMessagesCache();
    clearPostLoginPath();
    set(ACCOUNT_RESET);
    api.auth?.clearSession();
  },
  api,
  ...ACCOUNT_RESET,
  subscriptionPlans: [],
  // Background refreshes: a failure keeps the last value and stays out of the notifications.
  refreshUnreadCount: async () => {
    try {
      const res = await api.getUnreadCount({ onError: false });
      set({ unreadCount: res.unread });
    } catch {
      // keep the last known count
    }
  },
  refreshProblemsCount: async () => {
    try {
      const res = await api.getProblems({}, { onError: false });
      set({ problemsCount: res.data.filter((p) => p.group === 'action').length });
    } catch {
      // keep the last known count
    }
  },
  refreshFormsSummary: async () => {
    try {
      const formsSummary = await api.getFormsSummary({ onError: false });
      set({ formsSummary });
      return formsSummary;
    } catch {
      return get().formsSummary;
    }
  },
  refreshAccountBalance: async () => {
    try {
      const accountBalance = await api.getAccountBalance({ onError: false });
      set({ accountBalance });
    } catch {
      // keep the last known balance
    }
  },
  getAndSetVerifiedEmails: async (): Promise<VerifiedEmailsResponse> => {
    const res = await api.getVerifiedEmails();
    if (res.data) {
      set({ verifiedEmails: res.data });
      return res;
    }
    return {
      data: [],
      total: 0,
    };
  },

  getAndSetSubscriptionPlans: async () => {
    const subscriptionPlans = await api.getSubscriptionPlans();
    set({ subscriptionPlans });
    return subscriptionPlans;
  },

  getAndSetCurrentSubscription: async () => {
    if (!api.auth?.hasValidAccessToken()) {
      return new Promise((resolve) => {
        setTimeout(async () => {
          const result = await get().getAndSetCurrentSubscription();
          resolve(result);
        }, 500);
      });
    }
    const subscription = await api.getCurrentSubscription();
    set({ currentSubscription: subscription });
    return subscription;
  },

  subscribeToplan: async (planId: string) => {
    try {
      const result = await api.subscribeToplan(planId);

      if (result.type === 'Checkout') {
        window.location.href = result.content.redirect_url;
        return result;
      }

      await get().getAndSetCurrentSubscription();
      return result;
    } catch (error) {
      const response = (error as AxiosError<{ error?: string; action?: string }>).response;
      if (
        response?.status === 400 &&
        response.data?.error === 'CustomerPortalRequired' &&
        response.data?.action === 'redirect_to_customer_portal'
      ) {
        const portalSession = await api.createCustomerPortalSession();
        window.location.href = portalSession.portal_url;
        return;
      }

      throw error;
    }
  },
}));
